import React from 'react';
import PropTypes from 'prop-types';
import PaymentOptions from 'components/purchase/PaymentOptions';
import PaymentButton from 'components/PaymentButton';
import CheckoutNavigation from 'components/purchase/CheckoutNavigation';
import QuickCheckoutWarning from 'components/purchase/QuickCheckout/QuickCheckoutWarning';
import usePaymentSection from 'hooks/purchase/usePaymentSection';
import { isPayPalV6Enabled } from 'utils/paypalV6Config';
import { isApplePayAvailable } from 'utils/applePay';
import { isAdyenGooglePayEnabled } from 'utils/adyen';

/**
 * PaymentPanel component.
 * Render the payment options form and payment button as a self-contained unit.
 * Internally manages payment engine lifecycle (Evertec, MercadoPago, Cielo) and
 * all payment handlers via usePaymentSection, so the consumer only needs to
 * supply purchase/env/features context and action callbacks.
 * @param {object} props - purchase, env, features, purchaser, purchaserFormValues, cardValues, actions, cardErrorOccured, paymentEngine, selectedPaymentMethod, fetchingPayment, isUpdating, onBeforePaymentAsync, variant, collapsiblePurchaserForm.
 * @param {object} props.purchase - Purchase state object.
 * @param {object} props.env - Environment configuration.
 * @param {object} props.features - Whitelabel feature flags.
 * @param {object} props.purchaser - Derived purchaser data.
 * @param {object} props.purchaserFormValues - Raw redux-form values for the purchaser form.
 * @param {boolean} [props.legalTerms] - Current rendered legal acceptance.
 * @param {object} [props.quickCheckoutAgreementPolicy] - Quick Purchase agreement applicability.
 * @param {React.ReactNode} [props.purchaserAgreementContent] - Fields owned by the purchaser form.
 * @param {object} props.cardValues - Card form values.
 * @param {object} props.actions - Action callbacks.
 * @param {boolean} props.cardErrorOccured - Whether a card error has occurred.
 * @param {string} props.paymentEngine - Payment engine identifier.
 * @param {object} props.selectedPaymentMethod - Currently selected payment method.
 * @param {boolean} props.fetchingPayment - Whether a payment confirmation is in flight.
 * @param {boolean} props.isUpdating - Whether the purchase is being updated.
 * @param {Function} [props.onBeforePaymentAsync] - Optional async callback that must resolve before payment proceeds.
 * @param {string} [props.variant] - Button variant (e.g. 'quickPurchase').
 * @param {boolean} [props.collapsiblePurchaserForm] - Whether the purchaser form is collapsible.
 * @param {boolean} [props.purchaserFormDefaultExpanded] - When collapsible, start the purchaser form expanded.
 */
function PaymentPanel({
  purchase,
  env,
  features,
  purchaser,
  purchaserFormValues,
  legalTerms,
  cardValues,
  actions,
  cardErrorOccured,
  cardCheckoutMode,
  paymentEngine,
  selectedPaymentMethod,
  fetchingPayment,
  isUpdating,
  variant,
  collapsiblePurchaserForm,
  purchaserFormDefaultExpanded,
  isOrchestrating,
  orchestrationError,
  purchaserSubmitted,
  onPurchaserSubmitted,
  onBeforePaymentAsync,
  quickCheckoutAgreementPolicy,
  purchaserAgreementContent,
}) {
  const {
    handlePaymentClick,
    handleSubmitPurchaser,
    handlePurchaserSubmitFail,
    handleCardSubmit,
    handleTransferSubmit,
    handlePaymentTypeChange,
    buttonLoading,
    paymentBlocked,
    quickCheckoutAgreementsBlocked,
    beforeWalletPayment,
    yapeFormProps,
  } = usePaymentSection({
    purchase,
    env,
    features,
    purchaser,
    purchaserFormValues,
    legalTerms,
    quickCheckoutAgreementPolicy,
    actions,
    cardValues,
    fetchingPayment,
    isUpdating,
    selectedPaymentMethod,
    cardCheckoutMode,
    paymentEngine,
    isOrchestrating,
    orchestrationError,
    purchaserSubmitted,
    onPurchaserSubmitted,
    onBeforePaymentAsync,
  });

  const { availablePayments, paymentMethods, openTicket, total, selectedPaymentOption } = purchase;
  const paymentType = selectedPaymentOption || 'credit_card';
  // Hide main button when PayPal v6 or wallet SDK buttons render their own CTA.
  const shouldHideMainPaymentButton =
    (paymentType === 'paypal' && isPayPalV6Enabled()) ||
    (paymentType === 'apple_pay' && isApplePayAvailable()) ||
    (paymentType === 'google_pay' && isAdyenGooglePayEnabled());
  return (
    <>
      <PaymentOptions
        collapsiblePurchaserForm={collapsiblePurchaserForm}
        purchaserFormDefaultExpanded={purchaserFormDefaultExpanded}
        quickCheckoutAgreementPolicy={quickCheckoutAgreementPolicy}
        purchaserAgreementContent={purchaserAgreementContent}
        quickCheckoutAgreementsBlocked={quickCheckoutAgreementsBlocked}
        beforeWalletPayment={
          quickCheckoutAgreementPolicy?.hasAgreements ? beforeWalletPayment : undefined
        }
        availablePayments={availablePayments || []}
        paymentMethods={paymentMethods || []}
        submitPurchaser={handleSubmitPurchaser}
        onSubmitPurchaserFail={handlePurchaserSubmitFail}
        submitCard={handleCardSubmit}
        submitCardFail={actions.onFormFail}
        submitTransfer={handleTransferSubmit}
        selectedOption={paymentType}
        onPaymentTypeChange={handlePaymentTypeChange}
        isOpenTicket={openTicket || false}
        cardErrorOccured={cardErrorOccured}
        hideOptions={total === 0}
        isExchange={false}
        onSubmitForm={actions.onSubmitForm}
        purchaserUpdated={purchaserSubmitted}
        onPaymentButtonClick={handlePaymentClick}
        purchaseTotal={total}
        paymentEngine={paymentEngine}
        // YapeForm requires these and calls them on mount; PaymentOptions spreads them down.
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...yapeFormProps}
      />
      {variant === 'quickPurchase' && <QuickCheckoutWarning />}
      {!shouldHideMainPaymentButton && (
        <CheckoutNavigation>
          <PaymentButton
            variant={variant}
            paymentEngine={paymentEngine}
            paymentType={paymentType}
            totalPrice={String(total)}
            onClick={handlePaymentClick}
            isExchange={false}
            isLoading={buttonLoading}
            isDisabled={paymentBlocked}
          />
        </CheckoutNavigation>
      )}
    </>
  );
}

PaymentPanel.propTypes = {
  purchase: PropTypes.object.isRequired,
  env: PropTypes.object.isRequired,
  features: PropTypes.object.isRequired,
  purchaser: PropTypes.object.isRequired,
  purchaserFormValues: PropTypes.object,
  legalTerms: PropTypes.bool,
  cardValues: PropTypes.object,
  actions: PropTypes.object.isRequired,
  cardErrorOccured: PropTypes.bool,
  cardCheckoutMode: PropTypes.oneOf(['basis_theory', 'legacy']),
  paymentEngine: PropTypes.string,
  selectedPaymentMethod: PropTypes.object,
  fetchingPayment: PropTypes.bool,
  isUpdating: PropTypes.bool,
  variant: PropTypes.string,
  collapsiblePurchaserForm: PropTypes.bool,
  purchaserFormDefaultExpanded: PropTypes.bool,
  quickCheckoutAgreementPolicy: PropTypes.object,
  purchaserAgreementContent: PropTypes.node,
  isOrchestrating: PropTypes.bool,
  orchestrationError: PropTypes.object,
  purchaserSubmitted: PropTypes.bool,
  onPurchaserSubmitted: PropTypes.func,
  onBeforePaymentAsync: PropTypes.func,
};

PaymentPanel.defaultProps = {
  purchaserFormValues: null,
  cardValues: null,
  cardErrorOccured: false,
  cardCheckoutMode: 'legacy',
  paymentEngine: null,
  selectedPaymentMethod: null,
  fetchingPayment: false,
  isUpdating: false,
  variant: undefined,
  collapsiblePurchaserForm: false,
  purchaserFormDefaultExpanded: false,
  isOrchestrating: false,
  orchestrationError: null,
  purchaserSubmitted: false,
  onPurchaserSubmitted: null,
  onBeforePaymentAsync: null,
};

export default PaymentPanel;
